import React, { useEffect, useState } from 'react';
import {
  Badge, Button, Chip, CircularProgress, IconButton, Popover, Stack, Tooltip,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/NotificationsNone';
import { Link } from 'react-router-dom';
import iziToast from 'izitoast';

import { getAlerts, getAlertsCount, markAlertViewed } from '../../actions/journal';
import { internalServerError, journalSheetLink } from '../../utils/consts/pathRoutes';
import { formatDate, studentCardLink, studentName } from '../../utils/journalFormat';
import useIsMobile from '../../utils/useIsMobile';

// Счётчик обновляется раз в пять минут: уведомления заводит ночная проверка,
// дёргать однопроцессорный сервер чаще смысла нет.
const REFRESH_MS = 5 * 60 * 1000;

const EMAIL_LABEL = {
  'Отправлено': 'письмо отправлено',
  'Ошибка': 'письмо не ушло',
};

/**
 * Колокольчик ответственного — ТЗ 3.7.
 *
 * Серия пропусков без уважительной причины длиннее порога. Заказчик просил возможность
 * пометить уведомление просмотренным: сейчас этот процесс ведётся руками, и важно видеть,
 * что сигнал до кого-то дошёл.
 */
const AlertsBell = () => {
  const [count, setCount] = useState(0);
  const [anchor, setAnchor] = useState(null);
  const [items, setItems] = useState(null);
  const isMobile = useIsMobile();

  // На телефоне строки уведомления переносятся: в 320px имя с группой и ссылка
  // с кнопкой «Просмотрено» в одну строку не встают.
  const wrapRow = isMobile ? { flexWrap: 'wrap', rowGap: 0.5 } : {};

  const refreshCount = () => {
    getAlertsCount().then((result) => setCount(result.count)).catch(() => {});
  };

  useEffect(() => {
    refreshCount();
    const timer = setInterval(refreshCount, REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  const open = (event) => {
    setAnchor(event.currentTarget);
    setItems(null);
    getAlerts().then(setItems).catch(() => setItems([]));
  };

  const markViewed = (alert) => {
    markAlertViewed(alert.id)
      .then(() => {
        setItems((prev) => prev.map((item) => (
          item.id === alert.id ? { ...item, status: 'Просмотрено' } : item
        )));
        refreshCount();
      })
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }));
  };

  return (
    <>
      <Tooltip title="Пропуски без уважительной причины">
        <IconButton onClick={open} sx={{ mb: 0.5 }}>
          <Badge badgeContent={count} color="warning" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div style={{
          // maxWidth без box-sizing: ширина считается без отступов, поэтому 64px —
          // это 16px по краям экрана плюс 16px внутренних полей с каждой стороны.
          width: 420, maxWidth: 'calc(100vw - 64px)',
          maxHeight: 480, overflowY: 'auto', padding: 16,
        }}
        >
          <p className="journal-subtitle">Пропуски подряд</p>

          {items === null ? (
            <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={20} color="warning" /></Stack>
          ) : items.length === 0 ? (
            <div className="journal-muted">Уведомлений нет.</div>
          ) : (
            <Stack spacing={1}>
              {items.map((alert) => {
                const isNew = alert.status === 'Новое';

                return (
                  <div
                    key={alert.id}
                    className="journal-comment"
                    style={{ opacity: isNew ? 1 : 0.6, borderLeft: isNew ? '3px solid #FA7A45' : undefined }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, ...wrapRow }}>
                      <Link
                        className="journal-link" to={studentCardLink(alert)}
                        onClick={() => setAnchor(null)}
                      >
                        {studentName(alert)}
                      </Link>
                      <Chip size="small" variant="outlined" label={alert.group_name} />
                    </Stack>

                    <div>
                      <b>{alert.streak_days}</b> учебных дня(ей) подряд без причины,
                      {' '}с {formatDate(alert.first_date)} по {formatDate(alert.last_date)}
                    </div>

                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5, ...wrapRow }}>
                      <Link
                        className="journal-link" to={journalSheetLink(alert.group_id)}
                        onClick={() => setAnchor(null)}
                        style={{ fontSize: 12 }}
                      >
                        Открыть журнал
                      </Link>

                      {EMAIL_LABEL[alert.email_status] && (
                        <span className="journal-muted">· {EMAIL_LABEL[alert.email_status]}</span>
                      )}

                      {isNew ? (
                        <Button size="small" sx={{ ml: 'auto' }} onClick={() => markViewed(alert)}>
                          Просмотрено
                        </Button>
                      ) : (
                        <span className="journal-muted" style={{ marginLeft: 'auto' }}>
                          просмотрел {alert.viewed_by_name || '—'}
                        </span>
                      )}
                    </Stack>
                  </div>
                );
              })}
            </Stack>
          )}
        </div>
      </Popover>
    </>
  );
};

export default AlertsBell;
